import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import Sidebar from "../components/SideBar";
import CammyDock from "../components/CammyDock";
import { PageSectionsProvider } from "../context/PageSectionsContext";
import { CammySpeechProvider } from "../context/CammySpeechContext";
import { OnboardingTour, TOUR_STEPS } from "../onboarding";
import RedesignSidebar from "../redesign/components/SideBar";
import { getSourceName, setSourceName, getUserProfile } from "../services/authService";
import { PROPERTY_SOURCE_NAME } from "../services/propertyApiService";
import { getClient } from "../config/clients";
import { useIsDesktop } from "../hooks/useMediaQuery";

const isPropertyPath = (pathname) =>
  pathname.startsWith("/property-cases") ||
  pathname.startsWith("/redesign/property-cases");

/**
 * Shared chrome for the protected routes.
 *
 * Serves BOTH designs. The Clearline redesign brings its own sidebar and a
 * docked Cammy assistant, and is a light theme whose stylesheet sets the light
 * ground on `body` — so its chrome and theme class are applied only on
 * /redesign/* routes, leaving the existing dark design exactly as it was. That
 * isolation is the point: the two have to be comparable.
 *
 * PageSectionsProvider wraps both: it is how a page publishes its in-page
 * navigation targets for the dock to offer. Harmless for the existing design,
 * which publishes nothing.
 */
export default function Layout() {
  const { pathname } = useLocation();
  const onRedesign = pathname.startsWith("/redesign/");

  // The rail is an off-canvas drawer below lg and the static rail at lg,
  // driven from here because this is the only place either sidebar is
  // mounted — so one piece of state serves both designs.
  const [navOpen, setNavOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const closeNav = useCallback(() => setNavOpen(false), []);

  // A tapped link navigates but does not unmount the drawer, so close it here.
  useEffect(() => setNavOpen(false), [pathname]);

  // Motor and Property share one localStorage["source_name"] key, and it is
  // not just a display value: apiService.js's request interceptor calls
  // getApiBaseUrl() with NO argument, so it — and therefore which HOST every
  // request actually goes to — is read from this key on every single call,
  // regardless of what source_name a caller passes in the request body. So
  // both verticals' correctness depends entirely on this one key matching
  // whichever vertical is currently on screen.
  //
  // Rather than only restoring it when *leaving* Property (which left
  // Property itself unprotected against a direct nav/refresh/back-forward
  // landing on it with a stale "motor" value), enforce it deterministically
  // from the current path every time: property paths always force the
  // property source, everything else always forces the logged-in client's
  // own source. This is a useLayoutEffect so it lands before any page's own
  // data-fetching effect (a passive useEffect) runs — layout effects across
  // the whole tree commit before any passive effect does, regardless of
  // mount order.
  useLayoutEffect(() => {
    const current = getSourceName();
    // Which line the current client IS, straight from its JSON file. Property
    // does not live only at /property-cases: the post-login picker and the
    // sidebar's line switcher both send you to the shared /redesign/cases,
    // where the list decides what to render from this same field. Forcing the
    // profile's source on every non-property path undid that choice the moment
    // it was made, which is why choosing Property opened Motor.
    const currentLine = getClient(current)?.inspectionLine;

    const requiredSource = isPropertyPath(pathname)
      ? PROPERTY_SOURCE_NAME
      : currentLine === "property"
        ? current
        : getUserProfile()?.client_source_name;

    if (requiredSource && current !== requiredSource) {
      setSourceName(requiredSource);
    }
  }, [pathname]);

  return (
    <PageSectionsProvider>
      <CammySpeechProvider>
      <div
        className={[
          // Column on a phone (top bar above the page), today's row at lg.
          "flex flex-col lg:flex-row",
          // 100dvh, not 100vh: on iOS Safari 100vh is the URL-bar-expanded
          // height, so the bottom ~60-90px of the shell sits under the
          // toolbar — and with overflow-hidden on <main> nothing can scroll
          // it back into view.
          "h-[100dvh]",
          onRedesign
            ? "cl-root bg-cl-bg font-clearline text-cl-text"
            : "bg-background-dark",
        ].join(" ")}
      >
        {/* Phone-only top bar. The rails carry variable-length nav plus the
            line switcher, engine status and the user block, so there is no
            5-slot tab bar that could hold them — a drawer is the pattern that
            fits what is actually in there. */}
        <header
          className={[
            "flex h-14 flex-none items-center gap-3 border-b px-4 lg:hidden",
            onRedesign
              ? "border-cl-card bg-cl-sidebar"
              : "border-border-dark bg-background-dark",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className={[
              "-m-2 p-2",
              onRedesign ? "text-cl-text" : "text-white",
            ].join(" ")}
          >
            <Menu className="h-6 w-6" />
          </button>
          <span
            className={[
              "text-[13px] font-semibold",
              onRedesign ? "text-cl-text" : "text-white",
            ].join(" ")}
          >
            CamCom
          </span>
        </header>

        {/* Scrim. Rendered only while open so it cannot swallow taps. */}
        {navOpen && (
          <div
            className="fixed inset-0 z-fixed bg-black bg-opacity-50 lg:hidden"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
        )}

        {onRedesign ? (
          <RedesignSidebar open={navOpen} onNavigate={closeNav} />
        ) : (
          <Sidebar open={navOpen} onNavigate={closeNav} />
        )}

        {/* min-h-0 matters once the shell is a column: flex-1 defaults to
            min-height:auto, and the page's own scroller would then run past
            the bottom of the viewport with its last rows unreachable. */}
        <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Outlet /> {/* Nested routes render here */}
        </main>

        {/* Starts docked over the sidebar, draggable anywhere else, above the
            page but never blocking it (see CammyDock.jsx). Desktop only: it
            is 300x292 by inline style, which on a 375x812 phone would cover a
            third of the page with no room to drag it clear. */}
        {onRedesign && (
          <div className="hidden lg:block">
            <CammyDock />
          </div>
        )}

        {/* The tour points at data-tour attributes on the current-design rail.
            Below lg that rail is a closed drawer, so every target is
            off-screen; joyride positions in JS, so a CSS gate would not stop
            it. `ready` is the component's own hook for this. */}
        {!onRedesign && (
          <OnboardingTour
            tourKey="sidebar"
            steps={TOUR_STEPS.sidebar}
            ready={isDesktop}
          />
        )}

      </div>
      </CammySpeechProvider>
    </PageSectionsProvider>
  );
}
